import { any, func, object } from 'prop-types';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';

import Loading from '@components/Share/Loading';
import { useTrackingLoginWithPhoneNumber } from '@components/SingleSignOn/RegisterLogin/Login/hooks';
import {
  GET_LOGIN_OTP_USE_FOR_REGISTER_KEY,
  useGetLoginOTP
} from '@components/SingleSignOn/RegisterLogin/Login/RequireVerifyLoginForm/LoginWithPhone/services/useGetLoginOtp';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import { MY_SITE_NAME } from '@utils/constant';
import { useIsAtSiteName } from '@utils/customsHook/useIsAtSiteName';

import { PhoneNumberInput } from './PhoneNumberInput';
import { ValidatePhoneNumber } from './ValidatePhoneNumber';

const initialState = {
  inputData: null,
  formikValues: null,
  formikTouched: null,
  formikErrors: null,
  settingGlobal: null,
  verifyOtpErrorMsg: '',
  fieldPhoneCode: null,
  setFieldValue: () => {},
  setIsGetRegisterOtpSuccess: () => {}
};
const VerifyPhoneNumberUpdateContext = createContext(initialState);

export const useVerifyPhoneNumberUpdate = () => useContext(VerifyPhoneNumberUpdateContext);

const VerifyPhoneNumberUpdate = ({
  inputData,
  formikValues,
  formikTouched,
  formikErrors,
  settingGlobal,
  fieldPhoneCode,
  messageObject,
  setFieldValue,
  setErrors,
  setTouched,
  accountUser,
  formikValidateFieldValue
}) => {
  const getLoginOtpService = useGetLoginOTP();
  const tempPhoneNumberValue = useRef(accountUser?.PhoneNumber || '');
  const [verifyOtpErrorMsg, setVerifyOtpErrorMsg] = useState(null);
  const [isGettingRegisterOtp, setIsGettingRegisterOtp] = useState(false);
  const [isGetRegisterOtpSuccess, setIsGetRegisterOtpSuccess] = useState(false);
  const { trackingLoginWithPhoneData } = useTrackingLoginWithPhoneNumber();
  const isMySite = useIsAtSiteName(MY_SITE_NAME);

  const isEditingPhoneNumber = useMemo(
    () =>
      formikValues?.phone !== fieldPhoneCode.current?.value ||
      (!isMySite ? accountUser?.PhoneNumber : accountUser?.MYSitePhoneNumber) !== `${formikValues?.phoneCode}${formikValues?.phone}`,
    [
      formikValues?.phone,
      formikValues?.phoneCode,
      fieldPhoneCode.current?.value,
      fieldPhoneCode.current?.code,
      accountUser?.PhoneNumber,
      accountUser?.MYSitePhoneNumber
    ]
  );

  const isShowGetOTPButton = useMemo(
    () => (!accountUser?.IsPhoneVerified ? !isGetRegisterOtpSuccess : !isGetRegisterOtpSuccess && isEditingPhoneNumber),
    [accountUser?.IsPhoneVerified, isGetRegisterOtpSuccess, isEditingPhoneNumber]
  );

  useEffect(() => {
    if (!isEditingPhoneNumber) setIsGetRegisterOtpSuccess(false);
  }, [isEditingPhoneNumber]);

  useEffect(() => {
    if (tempPhoneNumberValue.current !== `${formikValues.phoneCode}${formikValues.phone}`) {
      tempPhoneNumberValue.current = `${formikValues.phoneCode}${formikValues.phone}`;

      setIsGetRegisterOtpSuccess(false);
    }
  }, [tempPhoneNumberValue.current, formikValues?.phoneCode, formikValues?.phone]);

  const handleGetRegisterOTP = useCallback(async () => {
    if (formikValues.phone) {
      try {
        const isPhoneNumberValid = await formikValidateFieldValue('phone', formikValues);
        if (isPhoneNumberValid) {
          setIsGettingRegisterOtp(true);

          setIsGetRegisterOtpSuccess(false);

          setVerifyOtpErrorMsg('');
          try {
            const phoneNumber = (fieldPhoneCode?.current?.code || '')?.replace('+', '') + formikValues.phone;

            const getLoginOtpResponse = await getLoginOtpService(phoneNumber, GET_LOGIN_OTP_USE_FOR_REGISTER_KEY);
            if (getLoginOtpResponse) setIsGetRegisterOtpSuccess(true);
          } catch (error) {
            setVerifyOtpErrorMsg(error);

            setIsGetRegisterOtpSuccess(false);
          } finally {
            setIsGettingRegisterOtp(false);
          }
        }
      } catch (error) {
        const errorMessage = error.message;
        if (typeof errorMessage === 'string') setVerifyOtpErrorMsg(errorMessage);
        if (errorMessage?.props?.messageCode) setVerifyOtpErrorMsg(messageObject?.[errorMessage.props.messageCode]);
      }
    } else {
      await setErrors({ phone: messageObject?.['Msg_020'] });

      await setTouched({ phone: true });
    }
  }, [formikValues.phone, fieldPhoneCode, setErrors, setTouched, messageObject]);

  return (
    <VerifyPhoneNumberUpdateContext.Provider
      value={{
        inputData,
        formikValues,
        formikTouched,
        formikErrors,
        settingGlobal,
        verifyOtpErrorMsg,
        fieldPhoneCode,
        setFieldValue,
        setIsGetRegisterOtpSuccess
      }}
    >
      <PhoneNumberInput />
      {isShowGetOTPButton ? (
        <div className='form-group osim-account-form__form__btn with-loading'>
          {isGettingRegisterOtp ? <Loading /> : <></>}
          <button
            type='button'
            disabled={isGettingRegisterOtp}
            className='osim-account-form__form__btn__item btn btn-primary'
            onClick={handleGetRegisterOTP}
          >
            <Text field={inputData?.['Get OTP Label']} />
          </button>
          {trackingLoginWithPhoneData?.isLoginWithPhoneFirst && trackingLoginWithPhoneData?.errorMessage ? (
            <span className='error-validate'>{trackingLoginWithPhoneData?.errorMessage}</span>
          ) : (
            <></>
          )}
        </div>
      ) : (
        isGetRegisterOtpSuccess && <ValidatePhoneNumber />
      )}
    </VerifyPhoneNumberUpdateContext.Provider>
  );
};

VerifyPhoneNumberUpdate.propTypes = {
  inputData: object,
  formikValues: object,
  formikErrors: object,
  formikTouched: object,
  fieldPhoneCode: any,
  setFieldValue: func,
  setErrors: func,
  setTouched: func,
  messageObject: object,
  settingGlobal: any,
  accountUser: object,
  formikValidateFieldValue: func
};

const mapStateToProps = (state) => ({
  settingGlobal: state.settingGlobalReducer.settingGlobal,
  messageObject: state.getMessageReducer.objMessages || {},
  accountUser: state.singleSignOnReducer.userInfoReducer.accountUser
});

export default connect(mapStateToProps)(VerifyPhoneNumberUpdate);
